import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { AccountState } from 'src/app/store/account/account.state';
import { firstValueFrom } from 'rxjs';
import { filter, timeout } from 'rxjs/operators';

/**
 * Waits for AccountState to be fully initialized (account data, partners,
 * invites, and profiles all loaded) before a guard reads any signal values.
 */
async function waitForAccountInitialized(accountState: AccountState): Promise<void> {
  await firstValueFrom(
    toObservable(accountState.initialized).pipe(
      filter((initialized) => initialized === true),
      timeout(10000)
    )
  );
}

/**
 * Email Verified Guard
 *
 * Protects routes by ensuring that the user's email has been verified.
 * If the email has not been verified, redirects to the verify-email page
 * to complete the verification process.
 *
 * This guard:
 * 1. Waits for AccountState to be initialized
 * 2. Checks the isEmailVerified computed signal
 * 3. Redirects to /account/verify-email if email is not verified
 * 4. Allows access if email has been verified
 *
 * @example
 * // In app.routes.ts
 * {
 *   path: 'account',
 *   canActivate: [authGuard, emailVerifiedGuard],
 *   loadComponent: () => import('./account-detail.page')
 * }
 *
 * @returns true if email verified, false otherwise (with redirect)
 */
export const emailVerifiedGuard: CanActivateFn = async () => {
  const accountState = inject(AccountState);
  const router = inject(Router);

  console.log('[EmailVerifiedGuard] Checking email verification status');

  try {
    await waitForAccountInitialized(accountState);

    const isVerified = accountState.isEmailVerified();
    console.log('[EmailVerifiedGuard] Account initialized, isEmailVerified:', isVerified);

    if (!isVerified) {
      console.log('[EmailVerifiedGuard] Email not verified, redirecting to verify-email');
      router.navigate(['/account/verify-email']);
      return false;
    }

    console.log('[EmailVerifiedGuard] Email verified, allowing access');
    return true;
  } catch (error) {
    console.error('[EmailVerifiedGuard] Error checking account:', error);
    router.navigate(['/account/verify-email']);
    return false;
  }
};

export const emailNotVerifiedGuard: CanActivateFn = async () => {
  const accountState = inject(AccountState);
  const router = inject(Router);

  console.log('[EmailNotVerifiedGuard] Checking email verification status');

  try {
    await waitForAccountInitialized(accountState);

    const isVerified = accountState.isEmailVerified();
    console.log('[EmailVerifiedGuard] Account initialized, isEmailVerified:', isVerified);
    if (isVerified) {
      console.log('[EmailVerifiedGuard] Email verified, redirecting to /');
      router.navigate(['/']);
      return false;
    }

    console.log('[EmailVerifiedGuard] Email not verified, allowing access');
    return true;
  } catch (error) {
    console.error('[EmailVerifiedGuard] Error checking account:', error);
    router.navigate(['/']);
    return false;
  }
};